import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import HotelForm from './HotelForm';
import SpaForm from './SpaForm';
import TourForm from './TourForm';
import TravelPackageForm from './TravelPackageForm';
import { listOwnerDrafts } from '../hooks/useOwnerDraft';
import { readOwnerSession, saveOwnerSession } from '../hooks/useOwnerSession';
import PartnerShell from '../components/PartnerShell';
import PartnerAuth from '../components/PartnerAuth';
import './OwnerRegistration.css';

const LISTING_TYPES = ['hotel', 'spa', 'tour', 'travel-package'];

const OwnerRegistration = () => {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [ownerToken, setOwnerToken] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  // Shown once, right after a listing is submitted
  const [showSubmittedDialog, setShowSubmittedDialog] = useState(false);

  // An owner already logged in to the portal skips the OTP
  useEffect(() => {
    const session = readOwnerSession();
    if (session) {
      setPhone(session.phone);
      setOwnerToken(session.token);
      setStep('type');
    }
  }, []);

  const steps = useMemo(() => ([
    { key: 'phone', label: 'Phone' },
    { key: 'otp', label: 'Verify' },
    { key: 'type', label: 'Type' },
    { key: 'form', label: 'Details' }
  ]), []);

  // Listing types this phone already started on this device
  const savedDrafts = useMemo(
    () => (step === 'type' && phone ? listOwnerDrafts(phone, LISTING_TYPES) : []),
    [step, phone]
  );

  const activeIndex = useMemo(() => {
    if (step === 'success') return steps.length - 1;
    return Math.max(0, steps.findIndex((item) => item.key === step));
  }, [step, steps]);

  const resetMessages = () => {
    setError('');
    setMessage('');
  };

  const handleSendOtp = async (event) => {
    event.preventDefault();
    resetMessages();
    if (!phone.trim()) {
      setError('Please enter a phone number.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/public/owners/send-otp`, { phone });
      setMessage('OTP sent successfully.');
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    resetMessages();
    if (!otp.trim()) {
      setError('Please enter the OTP.');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/public/owners/verify-otp`, {
        phone,
        otp
      });
      const token = response.data?.token || '';
      setOwnerToken(token);
      // Keeps them logged in to the owner portal as well
      if (token) saveOwnerSession({ token, phone: phone.replace(/\D/g, '').slice(-10) });
      setMessage('Phone verified successfully.');
      setStep('type');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    resetMessages();
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/public/owners/send-otp`, { phone });
      setMessage('OTP resent successfully.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectType = (type) => {
    resetMessages();
    setSelectedType(type);
    setStep('form');
  };

  const handleFormSuccess = () => {
    resetMessages();
    setStep('success');
    setShowSubmittedDialog(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToType = () => {
    resetMessages();
    setStep('type');
  };

  const handleRestart = () => {
    resetMessages();
    setStep('phone');
    setOtp('');
    setOwnerToken('');
    setSelectedType('');
  };

  const typeLabel = { hotel: 'Stay', spa: 'Spa', tour: 'Tour', 'travel-package': 'Travel Package' }[selectedType] || '';

  const barAction = ownerToken ? (
    <Link to="/owner" className="partner-bar-action">My listings</Link>
  ) : (
    <Link to="/owner" className="partner-bar-action">Partner login</Link>
  );

  // Phone and OTP steps: the full-page sign-in, laid out like /sy-admin/login
  if (step === 'phone' || step === 'otp') {
    return (
      <PartnerAuth
        side={{
          line: 'List your property',
          points: [
            'Free to list, with no commission on your bookings',
            'Customers message you directly on WhatsApp',
            'Our team reviews your listing and it goes live within 72 hours',
          ],
        }}
        card={{ title: 'Get started', text: 'Enter your phone number and we will send a one-time password to verify it.' }}
        footer={{ text: 'Already listed with us?', to: '/owner', link: 'Log in to manage your listings' }}
        progress={`Step ${activeIndex + 1} of ${steps.length}`}
        step={step}
        phone={phone}
        otp={otp}
        loading={loading}
        error={error}
        message={message}
        onPhoneChange={setPhone}
        onOtpChange={setOtp}
        onSendOtp={handleSendOtp}
        onVerifyOtp={handleVerifyOtp}
        onResendOtp={handleResendOtp}
        onChangePhone={handleRestart}
      />
    );
  }

  return (
    <PartnerShell action={barAction} wide={step === 'form'}>
      {step !== 'success' && (
        <div className="partner-progress">
          <div className="partner-progress-text">
            <span>Step {activeIndex + 1} of {steps.length}</span>
            <strong>{steps[activeIndex].label}</strong>
          </div>
          <div className="partner-progress-bar">
            <span style={{ width: `${((activeIndex + 1) / steps.length) * 100}%` }} />
          </div>
        </div>
      )}

      {error && <div className="owner-alert owner-alert-error">{error}</div>}
      {message && <div className="owner-alert owner-alert-success">{message}</div>}

      {step === 'type' && (
        <div className="owner-card">
          <h2>What do you want to list?</h2>
          <p className="owner-card-subtitle">
            {savedDrafts.length > 0
              ? 'You have a saved draft - pick the same type to continue it.'
              : 'Pick one. You can add more later from My listings.'}
          </p>
          <div className="owner-type-grid">
            <button type="button" className="owner-type-card" onClick={() => handleSelectType('hotel')}>
              {savedDrafts.includes('hotel') && <span className="owner-type-badge">Saved draft</span>}
              <span className="owner-type-icon">🏨</span>
              <span className="owner-type-title">Stay</span>
              <span className="owner-type-desc">Hotel, resort, apartment or guest house</span>
            </button>
            <button type="button" className="owner-type-card" onClick={() => handleSelectType('spa')}>
              {savedDrafts.includes('spa') && <span className="owner-type-badge">Saved draft</span>}
              <span className="owner-type-icon">💆</span>
              <span className="owner-type-title">Spa</span>
              <span className="owner-type-desc">Spa, salon or wellness centre</span>
            </button>
            <button type="button" className="owner-type-card" onClick={() => handleSelectType('tour')}>
              {savedDrafts.includes('tour') && <span className="owner-type-badge">Saved draft</span>}
              <span className="owner-type-icon">🎫</span>
              <span className="owner-type-title">Tour</span>
              <span className="owner-type-desc">Tours, activities and tickets</span>
            </button>
            <button type="button" className="owner-type-card" onClick={() => handleSelectType('travel-package')}>
              {savedDrafts.includes('travel-package') && <span className="owner-type-badge">Saved draft</span>}
              <span className="owner-type-icon">✈️</span>
              <span className="owner-type-title">Travel Package</span>
              <span className="owner-type-desc">Holiday packages with itinerary</span>
            </button>
          </div>
        </div>
      )}

      {step === 'form' && (
        <>
          <div className="partner-form-head">
            <div>
              <h2>Register your {typeLabel}</h2>
              <p>Your progress is saved after every step, so you can come back later.</p>
            </div>
            <button type="button" className="owner-link" onClick={handleBackToType}>
              Change type
            </button>
          </div>
          <div className="owner-form-body">
            {selectedType === 'hotel' && (
              <HotelForm
                mode="owner"
                ownerToken={ownerToken}
                ownerPhone={phone}
                onSuccess={handleFormSuccess}
                onCancel={handleBackToType}
              />
            )}
            {selectedType === 'spa' && (
              <SpaForm
                mode="owner"
                ownerToken={ownerToken}
                ownerPhone={phone}
                onSuccess={handleFormSuccess}
                onCancel={handleBackToType}
              />
            )}
            {selectedType === 'tour' && (
              <TourForm
                mode="owner"
                ownerToken={ownerToken}
                ownerPhone={phone}
                onSuccess={handleFormSuccess}
                onCancel={handleBackToType}
              />
            )}
            {selectedType === 'travel-package' && (
              <TravelPackageForm
                mode="owner"
                ownerToken={ownerToken}
                ownerPhone={phone}
                onSuccess={handleFormSuccess}
                onCancel={handleBackToType}
              />
            )}
          </div>
        </>
      )}

      {showSubmittedDialog && (
        <div className="partner-dialog-backdrop" role="presentation">
          <div className="partner-dialog" role="dialog" aria-modal="true" aria-labelledby="submitted-title">
            <span className="partner-dialog-icon" aria-hidden="true">🎉</span>
            <h2 id="submitted-title">Property Submitted Successfully</h2>
            <p>Your property has been submitted successfully.</p>
            <p>Our team will review your listing, and it will be live within 72 hours after approval.</p>
            <p>We’ll notify you once your property is live.</p>
            <button type="button" className="btn btn-primary" onClick={() => setShowSubmittedDialog(false)} autoFocus>
              Ok
            </button>
          </div>
        </div>
      )}

      {step === 'success' && (
        <div className="owner-card owner-success-card">
          <span className="owner-success-icon">🎉</span>
          <h2>Listing submitted</h2>
          <p>
            Thank you. Our team will review your {typeLabel.toLowerCase() || 'listing'} and put it live soon.
            You can follow its status and make changes from My listings.
          </p>
          <div className="owner-actions owner-success-actions">
            <button type="button" className="btn btn-secondary" onClick={handleBackToType}>
              Add another listing
            </button>
            <Link to="/owner" className="btn btn-primary">See my listings</Link>
          </div>
        </div>
      )}
    </PartnerShell>
  );
};

export default OwnerRegistration;
